import { type PropsWithChildren, type ReactNode } from 'react';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { useTranslation } from 'next-i18next';
import { useLocale, useOpenWindow } from '@/hooks';
import { BoltFillColorMintIcon, BoltFillColorVioletIcon, BoltFillIcon, BookCoverFillIcon, MessageDotsFillIcon, PlayIcon } from '@/icons';
import { PlanChip } from '@/shared/components/Chip/PlanChip';
import { type PlanType } from '@/types';
import { type MapTemplate, type MapTemplateType } from '@/types/MapTemplate';
import { type CommonTranslationKeys } from '@/types/translation';
import { Button } from '@/ui_v3';
import { cn } from '@/utils';
import { MapTemplateSubmitAction } from './MapTemplateSubmitAction';
interface Props {
  map: MapTemplate;
  isChange?: boolean;
  onSubmit?: (mapTemplate: MapTemplate) => void;
}
export const MapTemplateDetail = ({
  map,
  isChange,
  onSubmit
}: Props) => {
  const {
    locale
  } = useLocale();
  const openWindow = useOpenWindow(false);
  const {
    name,
    description,
    previewPlayUrl
  } = map.serializedLocalize[locale] ?? map.serializedLocalize.en;
  return <div className="relative w-full max-w-[440px] shadow-sm" data-sentry-component="MapTemplateDetail" data-sentry-source-file="MapTemplateDetail.tsx">
      {previewPlayUrl && <ExperienceButton onClick={() => openWindow(previewPlayUrl)} />}
      <img className="min-h-[440px] w-full object-cover" src={map.detailsImage} alt={name} />

      <div className="absolute bottom-0 left-0 w-full">
        <GradientOverlay data-sentry-element="GradientOverlay" data-sentry-source-file="MapTemplateDetail.tsx" />
        <div className="flex flex-col gap-md bg-white px-lg pb-lg pt-md">
          <div className="flex flex-col items-center gap-2xs">
            <PlanChip className={cn('bg-transparent text-alternative border-opacity-black-2 rounded-full', map.templatePlanType === 'BASIC' && 'text-positive border-green-500', map.templatePlanType === 'PRO' && 'text-strong border-strong')} plan={map.templatePlanType} data-sentry-element="PlanChip" data-sentry-source-file="MapTemplateDetail.tsx" />
            <h2 className="text-heading-lg-extrabold">{name}</h2>
          </div>

          <div className="flex w-full flex-col gap-xs rounded-md bg-neutral px-lg py-md">
            {description.map((text, i) => <DescriptionItem key={i}>{text}</DescriptionItem>)}
            <MapTypeDescriptionItem type={map.type} data-sentry-element="MapTypeDescriptionItem" data-sentry-source-file="MapTemplateDetail.tsx" />
            <PlanTypeDescriptionItem type={map.templatePlanType} data-sentry-element="PlanTypeDescriptionItem" data-sentry-source-file="MapTemplateDetail.tsx" />
          </div>

          <MapTemplateSubmitAction map={map} isChange={isChange} onSubmit={onSubmit} data-sentry-element="MapTemplateSubmitAction" data-sentry-source-file="MapTemplateDetail.tsx" />
        </div>
      </div>
    </div>;
};
const GradientOverlay = () => <div className="relative -bottom-px h-[80px] bg-[linear-gradient(180deg,rgba(255,255,255,0)_0%,#FFF_90%)]" data-sentry-component="GradientOverlay" data-sentry-source-file="MapTemplateDetail.tsx" />;
const BaseDescriptionItem = (props: PropsWithChildren<{
  icon: ReactNode;
}>) => <div className="flex items-center gap-xs" data-sentry-component="BaseDescriptionItem" data-sentry-source-file="MapTemplateDetail.tsx">
    <div className="shrink-0">{props.icon}</div>
    <p className="text-body-sm-medium">{props.children}</p>
  </div>;
const DescriptionItem = (props: PropsWithChildren) => <BaseDescriptionItem icon={<MessageDotsFillIcon size="sm" fill={ICON_COLOR.assistive} />} data-sentry-element="BaseDescriptionItem" data-sentry-component="DescriptionItem" data-sentry-source-file="MapTemplateDetail.tsx">
    {props.children}
  </BaseDescriptionItem>;
const MapTypeDescriptionItem = (props: {
  type: MapTemplateType;
}) => {
  const {
    t
  } = useTranslation(['common']);
  return <BaseDescriptionItem icon={<BookCoverFillIcon size="sm" fill={ICON_COLOR.assistive} />} data-sentry-element="BaseDescriptionItem" data-sentry-component="MapTypeDescriptionItem" data-sentry-source-file="MapTemplateDetail.tsx">
      {t(`common:map.${props.type}.description` as CommonTranslationKeys)}
    </BaseDescriptionItem>;
};
const PlanTypeDescriptionItem = (props: {
  type: PlanType;
}) => {
  const {
    t
  } = useTranslation(['build']);
  switch (props.type) {
    case 'FREE':
      return <div className="flex items-center gap-2xs">
          <BoltFillIcon size="sm" fill={ICON_COLOR.assistive} />
          <p className="text-body-sm-medium text-default">
            {t('build:mapTemplate.availableForFree')}
          </p>
        </div>;
    case 'BASIC':
      return <div className="flex items-center gap-2xs">
          <BoltFillColorMintIcon size="sm" />
          <p className="text-body-sm-semibold text-semi-strong">
            {t('build:mapTemplate.availableFromBasic')}
          </p>
        </div>;
    case 'PRO':
      return <div className="flex items-center gap-2xs">
          <BoltFillColorVioletIcon size="sm" />
          <p className="text-body-sm-semibold text-strong">
            {t('build:mapTemplate.availableOnlyPro')}
          </p>
        </div>;
  }
};
const ExperienceButton = (props: {
  onClick?: () => void;
}) => {
  const {
    t
  } = useTranslation(['build']);
  return <Button intent="neutral" size="sm" rounded="full" className="absolute right-lg top-lg shadow-sm" onClick={props.onClick} data-sentry-element="Button" data-sentry-component="ExperienceButton" data-sentry-source-file="MapTemplateDetail.tsx">
      <PlayIcon size="sm" fill={ICON_COLOR.neutral} data-sentry-element="PlayIcon" data-sentry-source-file="MapTemplateDetail.tsx" />
      {t('build.mapTemplateModal.experience')}
    </Button>;
};